import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { HoursSummariesResponse } from '../interfaces/collaborator.interfaces';
import {
  HoursSummaries,
  HoursSummariesPayload,
  HourSummaryPayload,
} from '../interfaces/hoursCollaborators';
import { AssignHoursResponse } from '../interfaces/hoursSummariesResponse.interface';
import { setTeachersList } from './hoursTeacherSlice';

export const hoursTeachersTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Teachers', 'HoursSummariesFilter'],
});
// Define a service using a base URL and expected endpoints
export const hoursTeachersApi = hoursTeachersTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all hours summaries
     */
    getHoursTeachers: builder.query<
      HoursSummariesResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        period_id?: string | number;
        education_level_id?: string | number;
      }
    >({
      queryFn: async (
        { profileId, page, perPage, search, period_id, education_level_id },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<HoursSummariesResponse>('/hours-summaries', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: 'asc',
              search,
              period_id,
              education_level_id,
            },
          });

          dispatch(setTeachersList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Teachers', id } as const)),
              { type: 'Teachers', id: 'LIST' },
            ]
          : [{ type: 'Teachers', id: 'LIST' }],
    }),

    /**
     * @POST assignd hour to teacher
     */
    addHoursTeacher: builder.mutation<
      HoursSummaries,
      { profileId: number; hoursTeacherPayload: Partial<HoursSummaries> }
    >({
      queryFn: async ({ profileId, hoursTeacherPayload }) => {
        try {
          const { data } = await mainApi.post<HoursSummaries>('/hours-summaries', {
            user_profile_id: profileId,
            ...hoursTeacherPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Teachers', id: 'LIST' }],
    }),

    /**
     * @POST assignd hour to teacher
     */
    addHoursTeacherArray: builder.mutation<
      HoursSummariesPayload,
      { profileId: number; hoursTeacherPayload: Partial<HourSummaryPayload[]> }
    >({
      queryFn: async ({ profileId, hoursTeacherPayload }) => {
        try {
          const { data } = await mainApi.post<HoursSummariesPayload>('/hours-summaries', {
            user_profile_id: profileId,
            hourSummary: hoursTeacherPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Teachers', id: 'LIST' },
        { type: 'HoursSummariesFilter', id: 'LIST' },
      ],
    }),

    /**
     * @PUT update hours summaries
     */
    updateHourSummary: builder.mutation<
      HoursSummaries,
      { profileId: number; hourSummaryId: string; hourSummaryPayload: Partial<HoursSummaries> }
    >({
      queryFn: async ({ profileId, hourSummaryId, hourSummaryPayload }) => {
        try {
          const { data } = await mainApi.put<HoursSummaries>(`/hours-summaries/${hourSummaryId}`, {
            user_profile_id: profileId,
            ...hourSummaryPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Teachers', id: 'LIST' },
        { type: 'HoursSummariesFilter', id: 'LIST' },
      ],
    }),

    /**
     * @DELETE remove hour summary
     */
    deleteHourSummary: builder.mutation<
      HoursSummaries,
      { profileId: number; hourSummaryId: number }
    >({
      queryFn: async ({ profileId, hourSummaryId }) => {
        try {
          const { data } = await mainApi.delete(`/hours-summaries/${hourSummaryId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Teachers', id: 'LIST' },
        { type: 'HoursSummariesFilter', id: 'LIST' },
      ],
    }),

    /**
     * @GET all hours summaries filter by education_levels period and journey
     */
    getHoursSummariesFilter: builder.query<
      AssignHoursResponse,
      {
        profileId: number;
        type_working: string;
        coll_type: string;
        period_id: string;
        education_level: string;
        page?: number;
        size?: number;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { profileId, type_working, coll_type, period_id, education_level, page, size },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<AssignHoursResponse>('collaborators/hours-summaries', {
            params: {
              user_profile_id: profileId,
              type_working,
              coll_type,
              period_id,
              education_level,
              page,
              size,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result?.data?.map(({ id }) => ({ type: 'HoursSummariesFilter', id } as const)),
              { type: 'HoursSummariesFilter', id: 'LIST' },
            ]
          : [{ type: 'HoursSummariesFilter', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetHoursTeachersQuery,
  useAddHoursTeacherMutation,
  useAddHoursTeacherArrayMutation,
  useDeleteHourSummaryMutation,
  useUpdateHourSummaryMutation,
  useGetHoursSummariesFilterQuery,
} = hoursTeachersApi;
